import React from 'react';
import { X, User, Calendar } from 'lucide-react';

const PaymentDetails = ({ payment, onClose }) => {
  if (!payment) return null;

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'pending':
        return '⏳';
      case 'failed':
        return '✗';
      case 'refunded':
        return '↩';
      case 'cancelled':
        return '✕';
      default:
        return '?';
    }
  };

  // const getMethodIcon = (method) => {
  //   switch (method) {
  //     case 'credit_card':
  //     case 'debit_card':
  //       return <CreditCard className="h-5 w-5" />;
  //     case 'paypal':
  //       return (
  //         <div className="w-5 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
  //           P
  //         </div>
  //       );
  //     case 'bank_transfer':
  //       return (
  //         <div className="w-5 h-5 bg-green-600 rounded text-white text-xs flex items-center justify-center font-bold">
  //           B
  //         </div>
  //       );
  //     case 'cash':
  //       return <DollarSign className="h-5 w-5" />;
  //     default:
  //       return <CreditCard className="h-5 w-5" />;
  //   }
  // };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Payment Details
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Payment Info */}
          <div className="space-y-6">
            {/* Transaction ID and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction ID
                </label>
                <p className="text-sm text-gray-900 font-mono">
                  {payment.transactionId}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    payment.status
                  )}`}
                >
                  <span className="mr-1">{getStatusIcon(payment.status)}</span>
                  {payment.status.charAt(0).toUpperCase() +
                    payment.status.slice(1)}
                </span>
              </div>
            </div>

            {/* Amount and Currency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <p className="text-2xl font-bold text-gray-900">
                  {formatAmount(payment.amount, payment.currency)}
                </p>
                {payment.refundAmount > 0 && (
                  <p className="text-sm text-red-600 mt-1">
                    Refunded:{' '}
                    {formatAmount(payment.refundAmount, payment.currency)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fees
                </label>
                <p className="text-sm text-gray-900">
                  {formatAmount(payment.fees, payment.currency)}
                </p>
              </div>
            </div>

            {/* Customer Info */}
            <div className="border-t pt-4">
              <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                <User className="h-4 w-4 mr-2" />
                Customer Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <p className="text-sm text-gray-900">
                    {payment.customerName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <p className="text-sm text-gray-900">
                    {payment.customerEmail}
                  </p>
                </div>
              </div>
            </div>

            {/* Timing */}
            <div className="border-t pt-4">
              <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Timing
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created
                  </label>
                  <p className="text-sm text-gray-900">
                    {formatDate(payment.createdAt)}
                  </p>
                </div>
                {payment.processedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Processed
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatDate(payment.processedAt)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <p className="text-sm text-gray-900">{payment.description}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 pt-4 border-t flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
            {/* {payment.status === "completed" && payment.refundAmount === 0 && (
              <button className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                Process Refund
              </button>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;
